import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  Form,
  Button,
  InputGroup,
  Spinner,
  Badge,
  Row,
  Col
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faRobot,
  faUser,
  faHome
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../../config';
import './MLSChatbot.css';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  properties?: any[];
  actions?: Array<{
    type: string;
    label: string;
    payload: any;
  }>;
}

interface MLSChatbotProps {
  initialMessage?: string;
  onResponse?: (response: any) => void;
  onSearchStart?: () => void;
  onSearchComplete?: () => void;
}

export default function MLSChatbot({
  initialMessage,
  onResponse,
  onSearchStart,
  onSearchComplete
}: MLSChatbotProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'Hello! I\'m your MLS assistant. I can help you search for properties, check market trends, or answer questions about listings. How can I help you today?',
      timestamp: new Date(),
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle initial message if provided
  useEffect(() => {
    if (initialMessage && initialMessage.trim()) {
      // Add a small delay to ensure component is mounted
      setTimeout(() => {
        setInput(initialMessage);
        // Optionally auto-submit the initial message
        handleSend();
      }, 500);
    }
  }, []);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);
    if (onSearchStart) onSearchStart();

    try {
      // First call Theta AI for NLP processing
      const thetaResponse = await fetch(`${API_BASE}/api/theta-ai/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          query: input,
          userId: 'chatbot-user',
          sessionId: sessionStorage.getItem('sessionId') || `session-${Date.now()}`
        }),
      });
      
      const thetaData = await thetaResponse.json();
      
      // Then call chat/public/realestate to get properties
      const chatResponse = await fetch(`${API_BASE}/api/chat/public/realestate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: input,
          messages: messages.map(m => ({ role: m.role, content: m.content })),
          sessionId: sessionStorage.getItem('sessionId') || `session-${Date.now()}`,
          context: {
            source: 'aisearchportal',
            extractedInfo: thetaData.criteria || {},
            hasComprehensiveInfo: thetaData.criteria && (thetaData.criteria.city || thetaData.criteria.zipCode),
            shouldSearchProperties: true
          }
        }),
      });

      const chatData = await chatResponse.json();
      
      // Use chat response for display, enriched with properties
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: chatData.response || thetaData.aiResponse || 'I found some properties for you.',
        timestamp: new Date(),
        properties: chatData.properties || thetaData.properties || [],
        actions: chatData.actions || thetaData.actions || [],
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Call onResponse callback if properties were found
      if (chatData.properties && chatData.properties.length > 0 && onResponse) {
        onResponse({ 
          properties: chatData.properties,
          shouldSearchProperties: chatData.shouldSearchProperties,
          response: chatData.response 
        });
      } else if (thetaData.properties && thetaData.properties.length > 0 && onResponse) {
        onResponse({ 
          properties: thetaData.properties,
          response: thetaData.aiResponse 
        });
      }
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
      if (onSearchComplete) onSearchComplete();
    }
  };

  const handleAction = (action: any) => {
    if (action.type === 'search') {
      setInput(action.payload.query);
    }
  };

  const renderPropertyCard = (property: any) => (
    <Card className="property-card mb-2" style={{ width: '18rem' }}>
      <Card.Body>
        <div className="d-flex align-items-center mb-2">
          <FontAwesomeIcon icon={faHome} className="text-primary me-2" />
          <h6 className="mb-0">${property.price?.toLocaleString()}</h6>
        </div>
        <p className="text-muted small mb-1">
          {property.bedrooms} bed, {property.bathrooms} bath
        </p>
        <p className="text-muted small mb-1">
          {property.city}, {property.state}
        </p>
        <p className="text-muted small mb-1">
          {property.squareFeet?.toLocaleString()} sqft
        </p>
        <Button variant="primary" size="sm" className="mt-2">
          View Details
        </Button>
      </Card.Body>
    </Card>
  );

  return (
    <Card className="mls-chatbot-container" style={{ height: '600px' }}>
      <Card.Header className="bg-primary text-white">
        <h5 className="mb-0">MLS Property Assistant</h5>
      </Card.Header>
      
      <Card.Body className="d-flex flex-column p-0">
        <div className="flex-grow-1 overflow-auto p-3">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`d-flex mb-3 ${
                message.role === 'user' ? 'justify-content-end' : 'justify-content-start'
              }`}
            >
              <div className={`d-flex ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`} style={{ maxWidth: '70%' }}>
                <div className={`avatar-circle ${message.role === 'user' ? 'ms-2' : 'me-2'}`}>
                  <FontAwesomeIcon
                    icon={message.role === 'assistant' ? faRobot : faUser}
                    className={message.role === 'assistant' ? 'text-primary' : 'text-secondary'}
                  />
                </div>
                
                <div>
                  <Card
                    className={`message-bubble ${
                      message.role === 'user' ? 'bg-primary text-white' : 'bg-light'
                    }`}
                  >
                    <Card.Body className="p-2">
                      <p className="mb-0">{message.content}</p>
                      
                      {message.properties && message.properties.length > 0 && (
                        <div className="d-flex flex-wrap mt-2">
                          {message.properties.map((property, idx) => (
                            <React.Fragment key={idx}>
                              {renderPropertyCard(property)}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                      
                      {message.actions && message.actions.length > 0 && (
                        <div className="mt-2">
                          {message.actions.map((action, idx) => (
                            <Badge
                              key={idx}
                              bg="outline-primary"
                              className="me-1 cursor-pointer action-badge"
                              onClick={() => handleAction(action)}
                            >
                              {action.label}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                  
                  <small className="text-muted">
                    {message.timestamp.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </small>
                </div>
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="d-flex justify-content-start mb-3">
              <div className="d-flex align-items-center">
                <div className="avatar-circle me-2">
                  <FontAwesomeIcon icon={faRobot} className="text-primary" />
                </div>
                <Card className="bg-light">
                  <Card.Body className="p-2">
                    <Spinner animation="border" size="sm" />
                  </Card.Body>
                </Card>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <Card.Footer className="bg-white">
          <Form onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
            <InputGroup>
              <Form.Control
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about properties, neighborhoods, or market trends..."
                disabled={isTyping}
              />
              <Button
                variant="primary"
                onClick={handleSend}
                disabled={!input.trim() || isTyping}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
              </Button>
            </InputGroup>
          </Form>
        </Card.Footer>
      </Card.Body>
    </Card>
  );
}